import { getUrlParams } from '../../../../lib/utils/urlParams'
import { rapidReady } from '../../../../lib/utils/rapid'
import MathUtils from '../../../../lib/utils/math'
import { promiseWithTimeout } from '../../../../lib/utils/timeout_promise'

const VALIDATE_DELAY = 50
const IN_VIEW_Y_OFFSET = 200
const UP_VOTE = 'spot-im-user-up-vote-click'
const DOWN_VOTE = 'spot-im-user-down-vote-click'
const OPEN_PROFILE = 'spot-im-open-user-profile'
const START_LOGIN = 'spot-im-login-start'
const POST_LOGIN = 'spot-im-post-login'
const API_READY = 'spot-im-api-ready'
const LOGIN_URL_ID = 'comments-login-url'
const SORT_TYPE = 'spot-im-sort-by-select'
const CONVERSATION_LOADED = 'spot-im-conversation-loaded'
const MORE_COMMENTS = 'spot-im-show-more-comments-clicked'
const SHARE_COMMENT = 'spot-im-share-type'
const REPLY_COMMENT = 'spot-im-user-clicked-reply'
const TYPE_COMMENT = 'spot-im-current-user-typing-start'
const POST_COMMENT = 'spot-im-current-user-sent-message'
const SHOW_MORE_REPLY = 'spot-im-show-more-replies-clicked'
const MUTE = 'spot-im-clicked-mute-user'
const SETTINGS = 'spot-im-clicked-settings'
const SSO_PATH = '/nex/modules/spotim/v1?m_mode=json&isXHR=true'
const { AOL: { user: { guid: USER_GUID = '' } = {} } = {} } = window
const IS_LOGGED_IN = USER_GUID !== ''
const SPOTIM_BASE_DOMAIN = 'https://launcher.spot.im/spot/'
const allowedDomains = ['wwwaolcom', 'wwwaolcouk']
const UUID = (rapidConfig && rapidConfig.keys && rapidConfig.keys.pstaid) || ''
const COMMENTS_COUNT_MODULE_TIMEOUT = 3000
const baseRapidValues = {
  g: UUID,
  sec: 'cmmts',
  subsec: 'openweb',
  itc: 1,
  reply_lv: 0
}

const _fetchCommentsCountViaOpenWeb = async (config, postId) => {
  const {
    spotimConfig: { spotId },
    openweb: {
      hostname = 'open-api.spot.im',
      pathname = '/v1/messages-count',
      protocol = 'https:'
    } = {}
  } = config

  const queryParams = {
    spot_id: spotId,
    posts_ids: postId
  }

  try {
    const url = new URL(pathname, `${protocol}//${hostname}`)
    url.search = new URLSearchParams(queryParams).toString()

    const data = await fetch(url)
    const messagesData = await data.json()
    const { messages_count = {} } = messagesData
    return MathUtils.getFormattedCount(messages_count[postId]) || 0
  } catch (e) {
    console.error(`Error caught in _fetchCommentsCountViaOpenWeb request: ${e}`)
    return 0
  }
}

export default (() => {
  class Spotim {
    constructor({ selector }) {
      this.elems = [...document.querySelectorAll(selector)]
      if (!this.elems.length) {
        return
      }
      this.scrollToCommentsBtn = document.querySelector('.m-ScrollToComments .comments-button')
      this.isMulitInstance = this.elems.length > 1
      this.launcherLoaded = false
      this.enableAOLCommentsCount = null
      this.elems.forEach(elem => {
        const { config } = elem.dataset
        if (!config) {
          return console.error('Spotim: No module config Found')
        }

        try {
          elem._config = JSON.parse(config)
        } catch (e) {
          return console.error('Error parsing config for', elem)
        }

        elem._initialized = false
        const countElm = elem.querySelector('.m-spotim__cta--text')
        const { stateKey } = elem.dataset
        if (stateKey) {
          window.wafer.ready(async () => {
            const { throttle } = window.wafer.utils
            const container = elem.closest('.m-article-container')
            elem._validateWithThrottle = throttle(
              () => this.validate(elem, container),
              VALIDATE_DELAY,
              this
            )
            elem._config = this._mergeWaferStateConfig(elem)
            const {
              spotimConfig: { postId },
              enableAOLCommentsCount = false
            } = elem._config
            if (this.enableAOLCommentsCount === null) {
              this.enableAOLCommentsCount = enableAOLCommentsCount
            }
            elem.setAttribute('data-uuid', postId)
            if (enableAOLCommentsCount) {
              elem?.classList.add('enableAOLCommentsCount')
              const count = await promiseWithTimeout({
                timeout: COMMENTS_COUNT_MODULE_TIMEOUT,
                promise: _fetchCommentsCountViaOpenWeb(elem._config, postId),
                onTimeoutError: () => {
                  if (countElm) {
                    countElm.textContent = 0
                  }
                  console.error(
                    `Error: _fetchCommentsCountViaOpenWeb req failed to load within ${COMMENTS_COUNT_MODULE_TIMEOUT} ms.`
                  )
                }
              })
              if (countElm) {
                countElm.textContent = count
              }
            }
            window.addEventListener('scroll', elem._validateWithThrottle)
            elem._validateWithThrottle()
          })
        } else {
          const {
            spotimConfig: { postId },
            enableAOLCommentsCount = false
          } = elem._config
          if (this.enableAOLCommentsCount === null) {
            this.enableAOLCommentsCount = enableAOLCommentsCount
          }
          if (enableAOLCommentsCount) {
            elem?.classList.add('enableAOLCommentsCount')
            promiseWithTimeout({
              timeout: COMMENTS_COUNT_MODULE_TIMEOUT,
              promise: _fetchCommentsCountViaOpenWeb(elem._config, postId),
              onTimeoutError: () => {
                if (countElm) {
                  countElm.textContent = 0
                }
                console.error(
                  `Error: _fetchCommentsCountViaOpenWeb req failed to load within ${COMMENTS_COUNT_MODULE_TIMEOUT} ms.`
                )
              }
            }).then(count => {
              if (countElm) {
                countElm.textContent = count
              }
            })
          }
          this.loadSpotim(elem)
        }

        return elem
      })

      this.init()
    }

    init() {
      this.performLogout().then(() => this.performLogin())
      this.addEventListeners()
    }

    scrollEnsured(id = UUID) {
      const commentContainerNode = document.querySelector(`#m-spotim-comments[data-uuid='${id}']`)
      if (commentContainerNode) {
        const aolHeaderElm = document.querySelector('.m-AolHeader .m-AolHeader--innerWrapper')
        const headerHeight = aolHeaderElm ? aolHeaderElm.getBoundingClientRect().height : 80
        const y = commentContainerNode.getBoundingClientRect().top + window.scrollY - headerHeight
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }

    sendCustomTracking(name, params = {}) {
      if (name === 'click') {
        rapidReady(rapid => {
          rapid.beaconClick(params.sec, params.slk, params.itc, params, name)
        })
      } else {
        rapidReady(rapid => {
          rapid.beaconEvent(name, params)
        })
      }
    }

    addEventListeners() {
      // Tracking listeners
      document.addEventListener('commentCount:clicked', event => {
        if (event?.detail?.elem) {
          const { elem } = event.detail
          const {
            dataset: { uuid = '' }
          } = elem
          if (uuid) {
            const commentContainerNode = document.querySelector(
              `#m-spotim-comments[data-uuid='${uuid}']`
            )
            if (commentContainerNode._deferClickHandled === undefined) {
              commentContainerNode._deferClickHandled = false
            }
            rapidReady(rapid => {
              rapid.beaconClick(
                'content-canvas',
                'comments',
                0,
                { elm: 'cmmt_open', itc: 1, g: uuid, ll2: 'aol' } || {},
                null
              )
            })

            const deferBtn = commentContainerNode.querySelector('.m-spotim__cta')
            if (deferBtn && !commentContainerNode._deferClickHandled) {
              this._handleDeferClick(commentContainerNode, deferBtn)
              commentContainerNode._deferClickHandled = true
            }
            this.scrollEnsured(uuid)
          }
        }
      })
      document.addEventListener(POST_LOGIN, event => {
        this.sendCustomTracking('secvw', {
          elmt: 'ent',
          itc: 1,
          slk: POST_LOGIN
        })
      })
      document.addEventListener(OPEN_PROFILE, event => {
        this.sendCustomTracking('click', {
          elm: 'btn',
          elmt: 'prvw',
          itc: 1,
          slk: OPEN_PROFILE
        })
      })
      document.addEventListener(UP_VOTE, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'vote thumb_up',
          elm: 'cmmt_vote',
          msg_id: event.detail.message_id,
          rcv_msg_id: 'd95d46e2-993f-4d48-8de2-391b61c0526e',
          outcm: 'rate'
        })
      })
      document.addEventListener(DOWN_VOTE, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'vote thumb_down',
          elm: 'cmmt_vote',
          msg_id: event.detail.message_id,
          rcv_msg_id: 'd95d46e2-993f-4d48-8de2-391b61c0526e',
          outcm: 'rate'
        })
      })
      document.addEventListener(SORT_TYPE, event => {
        let sortType = event.detail.sortedBy.toLowerCase()
        sortType = sortType === 'best' ? 'top' : sortType
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: sortType,
          elm: 'cmmt_sort_btn',
          outcm: 'sort'
        })
      })
      document.addEventListener(SHARE_COMMENT, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: event.detail.type.toLowerCase(),
          elm: 'cmmt_share',
          msg_id: event.detail.message_id,
          outcm: 'open'
        })
      })
      document.addEventListener(REPLY_COMMENT, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'reply',
          elm: 'cmmt_reply'
        })
      })
      document.addEventListener(SHOW_MORE_REPLY, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'open reply',
          elm: 'cmmt_more',
          elmt: 'showreply',
          msg_id: event.detail.message_id,
          outcm: 'show'
        })
      })
      document.addEventListener(MORE_COMMENTS, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'load more',
          elm: 'cmmt_more',
          outcm: 'more'
        })
      })
      document.addEventListener(TYPE_COMMENT, event => {
        this.sendCustomTracking('click', {
          slk: 'text-input',
          elm: 'cmmt_text',
          g: UUID,
          sec: 'cmmts',
          subsec: 'openweb',
          itc: 1,
          outcm: 'focus'
        })
      })
      document.addEventListener(POST_COMMENT, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'post',
          elm: 'cmmt_post',
          outcm: 'post'
        })
      })
      document.addEventListener(MUTE, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'mute-intent',
          elm: 'cmmt_mute',
          outcm: 'close'
        })
      })
      document.addEventListener(SETTINGS, event => {
        this.sendCustomTracking('click', {
          ...baseRapidValues,
          slk: 'your settings',
          elm: 'cmmt_username',
          sec: 'cmmt_his',
          itc: 0
        })
      })
      document.addEventListener(CONVERSATION_LOADED, event => {
        const { postId = '' } = event.meta || {}
        if (postId && this.enableAOLCommentsCount) {
          setTimeout(() => this.scrollEnsured(postId), 500)
        }
        this.sendCustomTracking('openweb_module_view', {
          sec: 'cmmts',
          subsec: 'openweb',
          etrg: 'show',
          outcm: 'openweb_module_view'
        })
      })

      // Login listener
      document.addEventListener(START_LOGIN, () => {
        const baseLoginUrl = document.getElementById(LOGIN_URL_ID).value
        window.location = `${baseLoginUrl}&dest=${encodeURIComponent(window.location.href)}`
      })
    }

    performLogin() {
      const self = this
      const login = () => {
        const urlParams = getUrlParams()
        const domain = urlParams.get('domain') || ''
        const domainParam = allowedDomains.indexOf(domain) !== -1 ? `&domain=${domain}` : ''
        const signedInParam = IS_LOGGED_IN ? '&isSignedIn=true' : ''

        window.SPOTIM.startSSO((codeA, completeSSOCallback) =>
          fetch(`${SSO_PATH}${domainParam}&codeA=${codeA}${signedInParam}`, {
            headers: {
              'cache-control': 'no-cache'
            }
          })
            .then(response => response.json())
            .then(jsonResponse => {
              self.sendCustomTracking('secvw', {
                elmt: 'ent',
                itc: 1,
                slk: 'spot-im-post-sso'
              })
              // use the recieved codeB
              completeSSOCallback(jsonResponse.data.response.code_b)
            })
            .catch(err => console.error(err))
        )
      }
      if (window.SPOTIM && window.SPOTIM.startSSO && IS_LOGGED_IN) {
        login()
      } else {
        document.addEventListener(API_READY, login, false)
      }
    }

    performLogout() {
      return new Promise((resolve, reject) => {
        if (window.SPOTIM && window.SPOTIM.logout) {
          window.SPOTIM.logout().then(resolve)
        } else {
          document.addEventListener(
            API_READY,
            () => {
              window.SPOTIM.logout().then(resolve)
            },
            false
          )
        }
      })
    }

    loadSpotim(elem) {
      const deferBtn = elem.querySelector('.m-spotim__cta')
      if (!this.isMulitInstance && !deferBtn) {
        this._loadOpenWebLauncherScriptWithComments(elem)
        return
      }
      this._loadOpenWebLauncherScript(elem)

      if (deferBtn) {
        deferBtn.addEventListener(
          'click',
          () => {
            if (elem) {
              const {
                dataset: { uuid = '' }
              } = elem
              rapidReady(rapid => {
                rapid.beaconClick(
                  'view-cmnts-cta',
                  'show comments',
                  0,
                  { elm: 'cmmt_open', itc: 1, g: uuid, ll2: 'aol' } || {},
                  null
                )
              })
              this._handleDeferClick(elem, deferBtn)
            }
          },
          { once: true }
        )
      } else {
        this._loadOpenWebComments(elem)
      }
    }

    _handleDeferClick(elem, deferBtn) {
      this._loadOpenWebComments(elem)
      deferBtn?.parentNode?.removeChild(deferBtn)
    }

    validate(elem, container) {
      const { elementInView } = window.wafer.utils
      if (
        !elem._initialized &&
        elementInView(container || elem, { offsetY: IN_VIEW_Y_OFFSET }, window.wafer.base.viewport)
      ) {
        window.removeEventListener('scroll', elem._validateWithThrottle)
        elem._initialized = true
        this.loadSpotim(elem)
      }
    }

    _mergeWaferStateConfig(elem) {
      const { stateKey } = elem.dataset
      const { uuid, href, commentsAllowed = true } = window.wafer.base.state[stateKey] || {}

      const { _config = {} } = elem
      const { spotimConfig: baseData = {} } = _config

      const articleUrl = `${window.location.origin}${href}`

      return {
        ..._config,
        spotimConfig: {
          ...baseData,
          commentsAllowed,
          postId: uuid,
          postUrl: articleUrl
        }
      }
    }

    /* SpotIm was renamed to OpenWeb
     * There are multiple ways to load the comments module on the page.
     * These are specified in their docs: https://developers.openweb.com/docs/conversation
     * To support multiple uses cases the following methods are used:
     *  - _loadOpenWebLauncherScriptWithComments: this is the `standard` way to load the comments module where we load
     *                                             the launcher and comments in a single script tag.
     *                                             Should only be used if single instance is on the page.
     *  - _loadOpenWebLauncherScript: This loads just the launcher module and not the comments.
     *                                Used in cases where there are multiple instance on the page.
     *
     * - _loadOpenWebComments: This adds the comments module instance to the page.
     *                         Requires the common launcher to have been added.
     */

    _loadOpenWebLauncherScriptWithComments(elem) {
      const {
        spotimConfig: {
          commentsAllowed,
          spotId = '',
          postId = '',
          postUrl = '',
          messageCount = 3,
          importFacebookComments
        } = {}
      } = elem._config || {}
      const fbComments = importFacebookComments ? postUrl : ''
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.setAttribute('data-spotim-module', 'spotim-launcher')
      script.setAttribute('data-post-url', postUrl)
      script.setAttribute('data-post-id', postId)
      script.setAttribute('data-messages-count', messageCount)
      script.setAttribute('data-facebook-url', fbComments)
      script.src = `${SPOTIM_BASE_DOMAIN}${spotId}`
      if (commentsAllowed) elem.appendChild(script)
    }

    _loadOpenWebLauncherScript(elem) {
      if (this.launcherLoaded) return
      const { spotimConfig: { spotId = '' } = {} } = elem._config || {}
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.setAttribute('data-spotim-module', 'spotim-launcher')
      script.setAttribute('data-spotim-autorun', false)
      script.src = `${SPOTIM_BASE_DOMAIN}${spotId}`

      const firstScript = document.getElementsByTagName('script')[0]
      firstScript.parentNode.insertBefore(script, firstScript)
      this.launcherLoaded = true
    }

    _loadOpenWebComments(elem) {
      const {
        spotimConfig: {
          commentsAllowed,
          postId = '',
          postUrl = '',
          messageCount = 3,
          importFacebookComments
        } = {}
      } = elem._config || {}
      const fbComments = importFacebookComments ? postUrl : ''
      const commentsModule = document.createElement('div')
      commentsModule.setAttribute('data-spotim-module', 'conversation')
      commentsModule.setAttribute('data-post-url', postUrl)
      commentsModule.setAttribute('data-post-id', postId)
      commentsModule.setAttribute('data-messages-count', messageCount)
      commentsModule.setAttribute('data-facebook-url', fbComments)
      if (commentsAllowed) elem.appendChild(commentsModule)
    }
  }

  return Spotim
})()
